import { Button, ButtonProps, Icon } from '@chakra-ui/react';
import { MdCheck } from 'react-icons/md';
import { useState, useEffect, useRef } from 'react';

type SaveState = 'idle' | 'saving' | 'success';

interface AnimatedSaveButtonProps extends Omit<ButtonProps, 'onClick'> {
    /** Label shown in idle state */
    label?: string;
    /** Label shown in success state (default: saved checkmark) */
    successLabel?: string;
    /** Called when clicked. Return a promise — button shows success when it resolves. */
    onClick: () => Promise<void> | void;
    /** How long to show the success state in ms (default: 1500) */
    successDuration?: number;
}

/**
 * Save button that cycles through idle → saving (spinner) → success (checkmark) → idle.
 * More satisfying than a toast — the user sees confirmation exactly where they clicked.
 *
 * Usage:
 *   <AnimatedSaveButton label="Save" onClick={async () => { await save(); }} />
 */
export default function AnimatedSaveButton({
    label = 'Save',
    successLabel,
    onClick,
    successDuration = 1500,
    ...buttonProps
}: AnimatedSaveButtonProps) {
    const [state, setState] = useState<SaveState>('idle');
    const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

    useEffect(() => {
        return () => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
        };
    }, []);

    const handleClick = async () => {
        if (state !== 'idle') return;
        setState('saving');
        try {
            await onClick();
            setState('success');
            timeoutRef.current = setTimeout(() => setState('idle'), successDuration);
        } catch {
            setState('idle');
        }
    };

    const isSuccess = state === 'success';

    return (
        <Button
            {...buttonProps}
            variant={isSuccess ? 'solid' : (buttonProps.variant ?? 'brand')}
            colorScheme={isSuccess ? 'green' : (buttonProps.colorScheme ?? 'brand')}
            isLoading={state === 'saving'}
            onClick={handleClick}
            leftIcon={isSuccess ? <Icon as={MdCheck} w='18px' h='18px' /> : undefined}
            transition='all 0.2s ease'
        >
            {isSuccess ? (successLabel ?? label) : label}
        </Button>
    );
}
